import { Controller } from "@hotwired/stimulus";
import { useClickOutside } from "stimulus-use";
import { computePosition, autoPlacement, autoUpdate, flip, shift, size } from "@floating-ui/dom";

const MOBILE_MAX_WIDTH = 639;

export default class extends Controller {
  static targets = ["card", "content"];
  static values = {
    placement: String,
    autoplacement: Boolean,
    updateOnScroll: Boolean,
    closeOnClick: Boolean,
    noMobileFullscreen: Boolean,
    persist: Boolean,
  };

  connect() {
    window.addEventListener("floating-menu:close", () => this.hide())
  }

  get anchor() {
    return this.element.closest("[data-floating-menu-anchor]");
  }

  // Le bouton qui ouvre : il dit son état (aria-expanded), le CSS fait pivoter son chevron.
  get trigger() {
    return this.element.querySelector("[aria-haspopup]");
  }

  disconnect() {
    this.hide();
  }

  position() {
    computePosition(this.element, this.menu, {
      placement: this.placementValue || "right",
      middleware: [
        this.autoplacementValue ? autoPlacement() : flip({ padding: 8 }),
        shift({ padding: 8 }),
        size({
          padding: 8,
          apply({ availableHeight, elements }) {
            elements.floating.style.maxHeight = `${availableHeight}px`;
            elements.floating.style.overflowY = "auto";
          },
        }),
      ].filter(Boolean),
    }).then(({ x, y }) => {
      Object.assign(this.menu.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  }

  isMobile() {
    return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
  }

  setupMobileFullscreen() {
    this.mobileMode = true;
    this.menu.classList.add("floating-menu-mobile-fullscreen");

    const header = document.createElement("div");
    header.className = "floating-menu-mobile-header";

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "floating-menu-mobile-close";
    closeBtn.setAttribute("aria-label", "Fermer");
    closeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>`;
    closeBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.hide();
    });

    header.appendChild(closeBtn);
    this.menu.prepend(header);

    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }

  // Le bouton bascule : un clic sur un menu ouvert le ferme (le clic hors du panneau l'a
  // d'ailleurs déjà fermé — on ne le rouvre pas) ; sur un menu fermé, il l'ouvre.
  async toggle(event) {
    event.preventDefault();
    if (this.element.hasAttribute("data-open")) return this.hide();

    window.dispatchEvent(new Event("floating-menu:close"));

    // In persist mode, reuse existing menu if already created
    this.element.setAttribute("data-open", "");
    this.trigger?.setAttribute("aria-expanded", "true");
    if (this.persistValue && this.menu && this.menu.isConnected) {
      this.menu.removeAttribute("data-closing");
      this.menu.style.display = "";
      this.position();
      useClickOutside(this, { element: this.menu });
      return;
    }

    const content = this.contentTarget.innerHTML;
    const fragment = document.createRange().createContextualFragment(content);
    this.menu = fragment.querySelector("[data-floating-menu-target='menu']");
    this.anchor.appendChild(fragment);
    this.menu.onclick = () => {
      if (!this.closeOnClickValue) return;
      return setTimeout(() => this.hide(), 10);
    };
    if (this.menu) {
      if (this.isMobile() && !this.noMobileFullscreenValue) {
        this.setupMobileFullscreen();
      } else if (this.updateOnScrollValue) {
        this.cleanup = autoUpdate(this.element, this.menu, () => {
          this.position();
        });
      } else {
        this.position();
      }
      useClickOutside(this, { element: this.menu });
      this.menu.querySelector("[autofocus]")?.focus();
    }
  }

  clickOutside() {
    this.hide();
  }

  // Le panneau part — après son animation de sortie s'il en a une (le CSS la pose sur
  // data-closing, cf. Ui::MenuComponent) ; les menus sans animation partent tout de suite.
  dismiss(menu) {
    const done = () => {
      if (!this.persistValue) return menu.remove();
      menu.style.display = "none";
      menu.removeAttribute("data-closing");
    };
    menu.setAttribute("data-closing", "");
    if (getComputedStyle(menu).animationName === "none") return done();
    menu.addEventListener("animationend", done, { once: true });
    setTimeout(done, 300);
  }

  hide() {
    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }
    this.element.removeAttribute("data-open");
    this.trigger?.setAttribute("aria-expanded", "false");
    if (this.menu) {
      this.menu.dispatchEvent(new Event("floating-menu:hide", { bubbles: true }));
      this.dismiss(this.menu);
    }
    if (this.mobileMode) {
      document.body.style.overflow = this.previousBodyOverflow || "";
      this.mobileMode = false;
    }
  }
}
